// -----------------------------------------------------------
// Author  : Mathieu Bucher
// Resume  : Hook to fetch internal API URLs with Clerk auth and return blob URLs
// Date    : 2026-03-23
// Ref.    : feat/spike-pdf-vision
// -----------------------------------------------------------
import { useEffect, useState } from "react";
import { buildAuthHeaders, getBaseUrl } from "@noa/api-client";

// Cache resolved blob URLs to prevent refetching on component remount.
const blobUrlCache = new Map<string, string>();
// Cache in-flight fetches so concurrent mounts of the same URL (e.g. every
// item in a BOM timeline) share a single request instead of one fetch each.
const pendingFetches = new Map<string, Promise<string>>();

function isExternal(url: string): boolean {
  return url.startsWith("http://") || url.startsWith("https://");
}

/**
 * Resolve a relative /api/... path to the full API URL.
 * In production, the frontend and API are on different domains, so relative
 * URLs must be prefixed with the API base URL.
 */
function resolveApiUrl(url: string): string {
  if (isExternal(url)) return url;
  // Relative /api/... paths need the API base URL prefix
  if (url.startsWith("/api/")) {
    return `${getBaseUrl()}${url}`;
  }
  return url;
}

async function fetchBlobUrl(url: string): Promise<string> {
  const cached = blobUrlCache.get(url);
  if (cached) return cached;

  const existing = pendingFetches.get(url);
  if (existing) return existing;

  const request = (async () => {
    const headers = await buildAuthHeaders();
    const fullUrl = resolveApiUrl(url);
    const response = await fetch(fullUrl, { headers });

    if (!response.ok) throw new Error(`Failed to fetch blob: ${response.status}`);

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    blobUrlCache.set(url, objectUrl);
    return objectUrl;
  })();

  pendingFetches.set(url, request);

  try {
    return await request;
  } finally {
    // Whether it resolved (now in blobUrlCache) or rejected, drop the in-flight
    // entry so a later mount can retry a failed fetch.
    pendingFetches.delete(url);
  }
}

/**
 * Fetches an internal API URL with Clerk authentication and returns a blob URL.
 * External URLs (http/https) are returned as-is.
 * Returns null while loading or on error.
 */
export function useAuthenticatedUrl(url: string | null | undefined): string | null {
  const [blobUrl, setBlobUrl] = useState<string | null>(() => {
    if (!url) return null;
    if (isExternal(url)) return url;
    return blobUrlCache.get(url) ?? null;
  });

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!url) {
        setBlobUrl(null);
        return;
      }

      // External URLs don't need authenticated fetch
      if (isExternal(url)) {
        setBlobUrl(url);
        return;
      }

      try {
        const objectUrl = await fetchBlobUrl(url);
        if (!cancelled) setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) setBlobUrl(null);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [url]);

  return blobUrl;
}
